import {
  FinancialFactsSchema,
  countryOption,
  formatMoneyMinor,
  type CountryPackStatus,
  type CoverageArea,
  type CoverageAreaId,
  type CoverageState,
  type CoverageSummary,
  type DecisionPlan,
  type DeepReviewAnswer,
  type DerivedMetrics,
  type Explainability,
  type FactReference,
  type FinancialFacts,
  type FinancialPlan,
  type HouseholdDependencyLink,
  type HouseholdMember,
  type HouseholdSnapshot,
  type MissingFact,
  type MoneyPaise,
  type OutcomeId,
  type OutcomeSummary,
  type PillarId,
  type PillarResult,
  type PillarStatus,
  type Priority,
  type Quest,
  type RiskConfidence,
  type RiskFinding,
  type RiskHazard,
  type RiskSeverity,
  type RiskState,
  type RiskSummary,
  type RiskTimeHorizon,
  type SupportedCountryCode
} from "@moneymap/domain";

export const FINANCIAL_ENGINE_VERSION = "ENGINE-2026.09-v7";
export const PILLAR_ORDER = ["cash-flow", "safety-buffer", "health-protection", "life-protection", "debt", "goals", "future"] as const satisfies readonly PillarId[];

const MAX_MONEY = 100_000_000_000;
const seniorBands = new Set<HouseholdMember["ageBand"]>(["65-74", "75+"]);
const stateRank: Record<RiskState, number> = { act_first: 0, unknown: 1, review_soon: 2, protected: 3 };
const severityRank: Record<RiskSeverity, number> = { critical: 0, high: 1, moderate: 2, low: 3 };
const horizonRank: Record<RiskTimeHorizon, number> = { immediate: 0, one_year: 1, long_term: 2 };
const confidenceGapRank: Record<RiskConfidence, number> = { unknown: 0, estimated: 1, confirmed: 2 };

export function clampScore(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function priorityForStatus(status: PillarStatus): Priority {
  switch (status) {
    case "urgent": return 1;
    case "attention": return 2;
    case "unknown": return 3;
    case "steady": return 4;
  }
}

function derivedDependencyLinks(facts: FinancialFacts): HouseholdDependencyLink[] {
  const memberIds = new Set(facts.householdMembers.map((member) => member.id));
  const supplied = (facts.dependencyLinks ?? []).filter((link) => memberIds.has(link.earnerMemberId) && memberIds.has(link.dependentMemberId));
  if (supplied.length > 0) return supplied;
  const earners = facts.householdMembers.filter((member) => member.earnsIncome);
  const dependants = facts.householdMembers.filter((member) => member.financiallyDependent);
  return dependants.flatMap((dependant) => earners.map((earner) => ({
    earnerMemberId: earner.id,
    dependentMemberId: dependant.id,
    confidence: "estimated" as const
  })));
}

export function deriveHouseholdContext(facts: FinancialFacts): HouseholdSnapshot {
  if (facts.householdMembers.length === 0) {
    return {
      members: [],
      dependencyLinks: [],
      memberCount: Math.max(1, facts.earners + facts.dependants),
      earnerCount: facts.earners,
      dependantCount: facts.dependants,
      childCount: 0,
      seniorParentCount: 0,
      uncoveredHealthCount: facts.healthCover === "none" ? Math.max(1, facts.earners + facts.dependants) : 0,
      unknownHealthCount: facts.healthCover === "unknown" ? Math.max(1, facts.earners + facts.dependants) : 0,
      unprotectedEarnerCount: facts.lifeCover === "none" ? facts.earners : 0,
      unknownEarnerProtectionCount: facts.lifeCover === "unknown" ? facts.earners : 0
    };
  }

  return {
    members: facts.householdMembers,
    dependencyLinks: derivedDependencyLinks(facts),
    memberCount: facts.householdMembers.length,
    earnerCount: facts.householdMembers.filter((member) => member.earnsIncome).length,
    dependantCount: facts.householdMembers.filter((member) => member.financiallyDependent).length,
    childCount: facts.householdMembers.filter((member) => member.relationship === "child").length,
    seniorParentCount: facts.householdMembers.filter((member) => member.relationship === "parent" && seniorBands.has(member.ageBand)).length,
    uncoveredHealthCount: facts.householdMembers.filter((member) => member.healthCoverage === "not-covered").length,
    unknownHealthCount: facts.householdMembers.filter((member) => member.healthCoverage === "unknown").length,
    unprotectedEarnerCount: facts.householdMembers.filter((member) => member.earnsIncome && (member.incomeProtection ?? (facts.lifeCover === "none" ? "not-covered" : facts.lifeCover === "unknown" ? "unknown" : "covered")) === "not-covered").length,
    unknownEarnerProtectionCount: facts.householdMembers.filter((member) => member.earnsIncome && (member.incomeProtection ?? (facts.lifeCover === "none" ? "not-covered" : facts.lifeCover === "unknown" ? "unknown" : "covered")) === "unknown").length
  };
}

export interface BaseRuleContext {
  facts: FinancialFacts;
  household: HouseholdSnapshot;
}

export interface MetricRuleSet {
  bufferTargetMonths: (context: BaseRuleContext) => number;
  lifeCoverSupportMonths: (context: BaseRuleContext) => number;
}

function boundedPolicyInteger(name: string, value: number, minimum: number, maximum: number): number {
  if (!Number.isInteger(value) || value < minimum || value > maximum) throw new Error(`${name} must be an integer between ${minimum} and ${maximum}.`);
  return value;
}

function cappedMoney(value: number): MoneyPaise {
  return Math.max(0, Math.min(MAX_MONEY, Math.round(value))) as MoneyPaise;
}

export function deriveMetrics(facts: FinancialFacts, household: HouseholdSnapshot, rules: MetricRuleSet): DerivedMetrics {
  const context = { facts, household };
  const monthlyOutgoPaise = cappedMoney(facts.essentialExpensesMonthlyPaise + facts.debtPaymentsMonthlyPaise);
  const monthlySurplusPaise = facts.incomeMonthlyPaise - monthlyOutgoPaise;
  const debtPaymentRatioBps = facts.incomeMonthlyPaise > 0 ? Math.round((facts.debtPaymentsMonthlyPaise / facts.incomeMonthlyPaise) * 10_000) : null;
  const runwayMonthsHundredths = monthlyOutgoPaise > 0 ? Math.round((facts.liquidCashPaise / monthlyOutgoPaise) * 100) : null;
  const runwayDays = runwayMonthsHundredths === null ? null : Math.round((runwayMonthsHundredths / 100) * 30);
  const bufferTargetMonths = boundedPolicyInteger("bufferTargetMonths", rules.bufferTargetMonths(context), 1, 60);
  const lifeCoverSupportMonths = boundedPolicyInteger("lifeCoverSupportMonths", facts.lifeProtectionDetails?.dependantSupportMonths ?? rules.lifeCoverSupportMonths(context), 0, 1_200);
  const bufferTargetPaise = cappedMoney(monthlyOutgoPaise * bufferTargetMonths);
  const bufferGapPaise = cappedMoney(bufferTargetPaise - facts.liquidCashPaise);
  const healthDetails = facts.healthProtectionDetails;
  const healthCostScenarioPaise = healthDetails && healthDetails.stressTestAmountPaise > 0 ? healthDetails.stressTestAmountPaise : null;
  const healthCostSharePaise = healthCostScenarioPaise === null ? 0 : Math.min(healthCostScenarioPaise, healthDetails?.estimatedCostSharePaise ?? 0);
  const healthEstimatedCoverCapacityPaise = healthCostScenarioPaise === null
    ? cappedMoney(facts.healthCoverAmountPaise)
    : cappedMoney(Math.min(facts.healthCoverAmountPaise, healthCostScenarioPaise - healthCostSharePaise));
  const healthEstimatedHouseholdExposurePaise = healthCostScenarioPaise === null
    ? null
    : cappedMoney(healthCostScenarioPaise - healthEstimatedCoverCapacityPaise);
  const healthCoverageRatioBps = healthCostScenarioPaise === null
    ? null
    : Math.round((healthEstimatedCoverCapacityPaise / healthCostScenarioPaise) * 10_000);
  const lifeDetails = facts.lifeProtectionDetails;
  const lifeImmediateNeedsPaise = cappedMoney(
    facts.outstandingDebtPaise
    + (lifeDetails?.immediateCostsPaise ?? 0)
    + (lifeDetails?.educationObligationsPaise ?? 0)
    + (lifeDetails?.otherObligationsPaise ?? 0)
  );
  const lifeOngoingNeedsPaise = cappedMoney(household.dependantCount > 0 ? facts.essentialExpensesMonthlyPaise * lifeCoverSupportMonths : 0);
  const lifeAvailableResourcesPaise = cappedMoney(
    (lifeDetails?.availableAssetsPaise ?? 0)
    + ((lifeDetails?.survivorIncomeMonthlyPaise ?? 0) * Math.min(lifeDetails?.survivorIncomeMonths ?? 0, lifeCoverSupportMonths))
    + (lifeDetails?.employerOrPublicBenefitsPaise ?? 0)
  );
  const lifeCoverPlanningNeedPaise = cappedMoney(lifeImmediateNeedsPaise + lifeOngoingNeedsPaise - lifeAvailableResourcesPaise);
  const lifeCoverGapPaise = cappedMoney(lifeCoverPlanningNeedPaise - facts.lifeCoverAmountPaise);
  const goalFundingGapPaise = cappedMoney(facts.goalTargetPaise - facts.goalCurrentSavingsPaise);

  return {
    monthlyOutgoPaise,
    monthlySurplusPaise,
    debtPaymentRatioBps,
    runwayMonthsHundredths,
    runwayDays,
    bufferTargetMonths,
    bufferTargetPaise,
    bufferGapPaise,
    healthCostScenarioPaise,
    healthEstimatedCoverCapacityPaise,
    healthEstimatedHouseholdExposurePaise,
    healthCoverageRatioBps,
    lifeCoverSupportMonths,
    lifeImmediateNeedsPaise,
    lifeOngoingNeedsPaise,
    lifeAvailableResourcesPaise,
    lifeCoverPlanningNeedPaise,
    lifeCoverGapPaise,
    goalFundingGapPaise
  };
}

export interface FinancialRuleContext extends BaseRuleContext {
  metrics: DerivedMetrics;
}

export type PillarRuleResult = Pick<PillarResult, "score" | "status" | "confidence" | "summary">;
export type CountryQuestDefinition = Omit<Quest, "pillar" | "priority" | "explainability"> & { explainability: Omit<Explainability, "rulesetVersion"> };

export interface CountryPillarRule {
  label: string;
  evaluate: (context: FinancialRuleContext) => PillarRuleResult;
  quest: (context: FinancialRuleContext, pillar: PillarResult) => CountryQuestDefinition;
}

export interface CountryRulePackV2 {
  schemaVersion: "2";
  id: string;
  version: string;
  localizationStatus: CountryPackStatus;
  review: {
    reviewedOn: string;
    nextReviewOn: string;
    healthSystemQuestions: string[];
    insuranceTerminology: Record<string, string>;
    policyDetailRequirements: string[];
    retirementContext: string;
    unsupportedAreas: string[];
  };
  matchesCountry: (country: SupportedCountryCode) => boolean;
  validateFacts: (facts: FinancialFacts) => string | null;
  metrics: MetricRuleSet;
  pillars: Record<PillarId, CountryPillarRule>;
  disclaimer: (context: FinancialRuleContext) => string;
}

export type CountryRuleSet = CountryRulePackV2;

const pillarStatusRank: Record<PillarStatus, number> = { urgent: 0, attention: 1, unknown: 2, steady: 3 };
const outcomeForPillar: Record<PillarId, OutcomeId> = {
  "cash-flow": "today",
  "safety-buffer": "shock",
  "health-protection": "protect",
  "life-protection": "protect",
  debt: "today",
  goals: "future",
  future: "future"
};
const hazardForPillar: Record<PillarId, RiskHazard> = {
  "cash-flow": "cashflow",
  "safety-buffer": "income_loss",
  "health-protection": "health_cost",
  "life-protection": "death",
  debt: "debt",
  goals: "goal",
  future: "retirement"
};

function riskState(status: PillarStatus): RiskState {
  return status === "urgent" ? "act_first" : status === "attention" ? "review_soon" : status === "steady" ? "protected" : "unknown";
}

function riskConfidence(confidence: PillarResult["confidence"]): RiskConfidence {
  return confidence === "high" ? "confirmed" : confidence === "medium" ? "estimated" : "unknown";
}

function severityFor(pillar: PillarResult): RiskSeverity {
  if (pillar.status === "urgent" && ["cash-flow", "health-protection", "life-protection", "debt"].includes(pillar.id)) return "critical";
  if (pillar.status === "urgent" || (["health-protection", "life-protection"].includes(pillar.id) && pillar.status !== "steady")) return "high";
  if (pillar.status === "attention" || pillar.status === "unknown") return "moderate";
  return "low";
}

function horizonFor(pillar: PillarResult): RiskTimeHorizon {
  if (["cash-flow", "safety-buffer", "health-protection", "debt"].includes(pillar.id)) return "immediate";
  if (pillar.id === "life-protection") return "one_year";
  return "long_term";
}

function allMemberIds(household: HouseholdSnapshot): string[] {
  return household.members.map((member) => member.id);
}

function affectedMembers(pillar: PillarResult, household: HouseholdSnapshot): string[] {
  if (pillar.id === "health-protection") {
    const exposed = household.members.filter((member) => member.healthCoverage !== "covered").map((member) => member.id);
    return exposed.length > 0 ? exposed : allMemberIds(household);
  }
  if (pillar.id === "life-protection") {
    const dependants = household.members.filter((member) => member.financiallyDependent).map((member) => member.id);
    return dependants.length > 0 ? dependants : allMemberIds(household);
  }
  return allMemberIds(household);
}

function moneyFact(key: string, label: string, value: number): FactReference {
  return { key, label, value, unit: "currency_minor" };
}

function countFact(key: string, label: string, value: number): FactReference {
  return { key, label, value, unit: "people" };
}

function formatMoney(context: FinancialRuleContext, value: number): string {
  return formatMoneyMinor(value, context.facts.currency, countryOption(context.facts.country).locale);
}

function relationshipPhrase(member: HouseholdMember): string {
  if (member.relationship === "self") return "you";
  if (member.relationship === "partner") return "your partner";
  if (member.relationship === "child") return "your child";
  if (member.relationship === "parent" && seniorBands.has(member.ageBand)) return "your older parent";
  if (member.relationship === "parent") return "your parent";
  return "a person you support";
}

function affectedPhrase(memberIds: string[], household: HouseholdSnapshot): string {
  const members = household.members.filter((member) => memberIds.includes(member.id));
  if (members.length === 1) return relationshipPhrase(members[0]!);
  if (members.length > 1) return `${members.length} people in your household`;
  return household.dependantCount > 0 ? `${household.dependantCount} people who rely on household income` : "your household";
}

function healthMissingFacts(context: FinancialRuleContext): MissingFact[] {
  const missing: MissingFact[] = [];
  if (context.household.unknownHealthCount > 0 || context.facts.healthCover === "unknown") missing.push({ key: "health-coverage-status", label: "Who is covered", reason: "Coverage cannot be treated as protection until each household member is checked." });
  const review = context.facts.healthProtectionReview;
  const checks: Array<[keyof NonNullable<FinancialFacts["healthProtectionReview"]>, string]> = [
    ["deductibleOrCopayReviewed", "Deductible or co-payment"],
    ["roomAndSublimitsReviewed", "Room limits and sub-limits"],
    ["waitingPeriodsReviewed", "Waiting periods"],
    ["exclusionsReviewed", "Important exclusions"],
    ["networkReviewed", "Hospital/provider network"],
    ["continuityReviewed", "Renewal and employment continuity"]
  ];
  if (context.facts.healthCover !== "none" && context.facts.healthCover !== "unknown") {
    if (context.facts.healthCoverAmountPaise === 0) missing.push({ key: "health-cover-amount", label: "Usable benefit limit", reason: "A coverage type alone cannot be compared with a healthcare-cost scenario." });
    if (context.metrics.healthCostScenarioPaise === null) missing.push({ key: "health-stress-test", label: "Healthcare-cost stress test", reason: "MoneyMap needs an explicit bill amount before it can show whether the reported limit covers that scenario." });
    for (const [key, label] of checks) if (!review?.[key]) missing.push({ key: `health-${key}`, label, reason: "A reported policy may still leave this cost or access condition exposed." });
    for (const member of context.household.members.filter((item) => item.healthCoverage === "covered" && !item.healthCoverageSources?.length)) missing.push({ key: `health-source-${member.id}`, label: "Coverage source for a household member", reason: "Public, employer and private arrangements can behave differently during a shock." });
    for (const member of context.household.members.filter((item) => item.healthCoverageSources?.includes("employer") && item.healthCoverageEndsWithEmployment !== false)) missing.push({ key: `health-employment-${member.id}`, label: "Job-loss continuity", reason: "Employer-linked coverage may end at the same time as household income." });
  }
  return missing;
}

function lifeMissingFacts(context: FinancialRuleContext): MissingFact[] {
  const missing: MissingFact[] = [];
  if (context.facts.lifeCover === "unknown") missing.push({ key: "life-cover-type", label: "Existing income-replacement cover", reason: "The current protection arrangement has not been confirmed." });
  if (context.facts.lifeCover !== "none" && context.facts.lifeCoverAmountPaise === 0) missing.push({ key: "life-cover-amount", label: "Existing death benefit", reason: "A cover type was reported without a usable benefit amount." });
  if (!context.facts.lifeProtectionDetails) missing.push({ key: "life-needs-detail", label: "Survivor resources and specific obligations", reason: "The first-pass scenario cannot yet account for education, immediate costs, survivor income or other available assets." });
  else if (!context.facts.lifeProtectionDetails.nomineesAndTermReviewed) missing.push({ key: "life-term-nominees", label: "Policy term and nominees", reason: "The benefit must remain available for the period dependants need it and reach the intended people." });
  for (const member of context.household.members.filter((item) => item.earnsIncome && item.incomeProtection === undefined)) missing.push({ key: `income-protection-${member.id}`, label: "Protection for each income earner", reason: "The household-level answer has been used as an estimate; confirm each earner separately." });
  return missing;
}

function genericMissingFacts(pillar: PillarResult, context: FinancialRuleContext): MissingFact[] {
  if (pillar.id === "health-protection") return healthMissingFacts(context);
  if (pillar.id === "life-protection") return lifeMissingFacts(context);
  if (pillar.id === "debt" && (context.facts.hasOverdueDebt === null || context.facts.hasOverdueDebt === undefined)) return [{ key: "overdue-debt", label: "Overdue payment status", reason: "An overdue required payment would move debt ahead of longer-term planning." }];
  if (pillar.id === "goals" && (context.facts.primaryGoal === "none" || context.facts.goalTargetYears === 0)) return [{ key: "goal-definition", label: "Goal amount and date", reason: "A goal needs an amount and time horizon before progress can be measured." }];
  if (pillar.status === "unknown") return [{ key: `${pillar.id}-detail`, label: `${pillar.label} details`, reason: "More information is needed before this area can be assessed." }];
  return [];
}

function riskCopy(pillar: PillarResult, context: FinancialRuleContext): { headline: string; explanation: string } {
  const { facts, household, metrics } = context;
  switch (pillar.id) {
    case "cash-flow":
      return metrics.monthlySurplusPaise < 0
        ? { headline: `You’re short by about ${formatMoney(context, Math.abs(metrics.monthlySurplusPaise))} in a normal month.`, explanation: "The bills you listed are higher than the income you listed. Deal with that before adding a new savings or investment target." }
        : { headline: `About ${formatMoney(context, metrics.monthlySurplusPaise)} is left after the monthly bills you listed.`, explanation: pillar.status === "attention" ? "That doesn’t leave much room for a bill you weren’t expecting." : "On these numbers, the household has money left each month." };
    case "safety-buffer":
      return metrics.runwayDays === null
        ? { headline: "We can’t tell how long your savings would last yet.", explanation: "Add the essentials you pay each month and we can turn your savings into a number of days." }
        : { headline: `If income stopped, the money you can reach would last about ${metrics.runwayDays} days.`, explanation: `We divided your accessible savings by ${formatMoney(context, metrics.monthlyOutgoPaise)} of monthly essentials and required debt payments.` };
    case "health-protection": {
      const exposed = household.members.filter((member) => member.healthCoverage === "not-covered");
      const unknown = household.members.filter((member) => member.healthCoverage === "unknown");
      if (exposed.length > 0) return { headline: `${affectedPhrase(exposed.map((member) => member.id), household)} ${exposed.length === 1 ? "has" : "have"} no health-cost cover listed.`, explanation: "A large hospital bill could have to come from income, savings or new debt." };
      if (unknown.length > 0 || facts.healthCover === "unknown") return { headline: `We don’t know whether ${unknown.length || household.memberCount} ${unknown.length === 1 ? "person is" : "people are"} covered for healthcare costs.`, explanation: "Until this is checked, we can’t count them as protected." };
      const employmentLinked = household.members.filter((member) => member.healthCoverageSources?.includes("employer") && member.healthCoverageEndsWithEmployment !== false);
      if (facts.healthCover === "employer" || employmentLinked.length > 0) return { headline: `${affectedPhrase(employmentLinked.map((member) => member.id), household)} may lose health cover when the job ends.`, explanation: "That could remove both income and insurance at the same time. Check what happens after resignation, redundancy or retirement." };
      if (metrics.healthCostScenarioPaise !== null && metrics.healthEstimatedHouseholdExposurePaise !== null && metrics.healthEstimatedHouseholdExposurePaise > 0) return { headline: `For the hospital bill you entered, the household may still have to find ${formatMoney(context, metrics.healthEstimatedHouseholdExposurePaise)}.`, explanation: `We compared a ${formatMoney(context, metrics.healthCostScenarioPaise)} bill with the cover amount and out-of-pocket share you entered.` };
      if (metrics.healthCostScenarioPaise !== null && metrics.healthEstimatedHouseholdExposurePaise === 0) return { headline: "The cover amount you entered could pay the hospital bill you tested.", explanation: "Now check the policy details. Waiting periods, exclusions, room limits, hospital networks and job-linked cover can still change the answer." };
      const missingCount = healthMissingFacts(context).length;
      return { headline: missingCount > 0 ? `You have health cover, but ${missingCount} important policy details are still unchecked.` : "The first health-cover check is complete.", explanation: pillar.summary };
    }
    case "life-protection": {
      const people = affectedMembers(pillar, household);
      if (household.unprotectedEarnerCount > 0 && household.dependantCount > 0) return { headline: `${affectedPhrase(people, household)} rely on ${household.unprotectedEarnerCount === 1 ? "an earner who has" : `${household.unprotectedEarnerCount} earners who have`} no life or income-replacement cover listed.`, explanation: `We tested the debt plus ${metrics.lifeCoverSupportMonths} months of essential household spending.` };
      if (household.unknownEarnerProtectionCount > 0) return { headline: `We don’t know what the family would receive if ${household.unknownEarnerProtectionCount === 1 ? "one earner died or could not work" : `${household.unknownEarnerProtectionCount} earners died or could not work`}.`, explanation: "Check each earner separately. A total cover amount can hide one person who is not covered." };
      if (facts.lifeCover === "none" && household.dependantCount > 0) return { headline: `${affectedPhrase(people, household)} rely on household income, but no life cover is listed.`, explanation: `We tested the debt plus ${metrics.lifeCoverSupportMonths} months of essential household spending.` };
      if (facts.lifeCover === "unknown") return { headline: `We don’t know what ${affectedPhrase(people, household)} would receive if an earner died.`, explanation: "Check the cover type, benefit amount and any income or assets the family could still use." };
      if (metrics.lifeCoverGapPaise > 0) return { headline: `The family-support calculation is short by ${formatMoney(context, metrics.lifeCoverGapPaise)} for ${affectedPhrase(people, household)}.`, explanation: "This is a number to investigate, not a recommendation to buy a particular amount or product." };
      return { headline: "The life-cover amount you entered meets the family-support calculation.", explanation: "Now check the policy end date, nominees and exclusions—and update the numbers when family obligations change." };
    }
    case "debt":
      return facts.hasOverdueDebt
        ? { headline: "A debt payment is overdue.", explanation: "Bring required payments up to date before adding money to optional savings or investments." }
        : { headline: metrics.debtPaymentRatioBps === null ? "We can’t tell how hard the debt payments are hitting yet." : `${(metrics.debtPaymentRatioBps / 100).toFixed(0)}% of the income you entered goes to required debt payments.`, explanation: pillar.summary };
    case "goals":
      return facts.primaryGoal === "none" || facts.goalTargetYears === 0
        ? { headline: "You haven’t put a number and date on a goal yet.", explanation: "That can wait until any immediate household problems are clear." }
        : { headline: `You still need ${formatMoney(context, metrics.goalFundingGapPaise)} for the ${facts.primaryGoal.replace("-", " ")} goal.`, explanation: "That is simply the goal minus what you’ve saved. We have not assumed investment growth or inflation." };
    case "future":
      return { headline: pillar.status === "unknown" ? "We haven’t asked enough about retirement and long-term plans yet." : "Long-term planning still needs a local review.", explanation: pillar.summary };
  }
}

function scenarioValues(pillar: PillarResult, context: FinancialRuleContext): Pick<RiskFinding, "consequence" | "availableCapacity" | "gap" | "expectedDelta"> {
  const { facts, household, metrics } = context;
  switch (pillar.id) {
    case "cash-flow": return { consequence: { label: "Core monthly outgo", kind: "money", value: metrics.monthlyOutgoPaise, unit: "currency_minor" }, availableCapacity: { label: "Monthly income", kind: "money", value: facts.incomeMonthlyPaise, unit: "currency_minor" }, gap: { label: metrics.monthlySurplusPaise < 0 ? "Monthly shortfall" : "Monthly breathing room", kind: "money", value: Math.abs(metrics.monthlySurplusPaise), unit: "currency_minor" }, expectedDelta: { label: "Monthly shortfall", before: Math.max(0, -metrics.monthlySurplusPaise), after: 0, unit: "currency_minor" } };
    case "safety-buffer": {
      const nextDays = metrics.runwayDays === null ? 30 : metrics.runwayDays < 30 ? 30 : metrics.bufferTargetMonths * 30;
      return { consequence: { label: "Planning runway", kind: "duration", value: metrics.bufferTargetMonths, unit: "months" }, availableCapacity: { label: "Accessible runway", kind: "duration", value: metrics.runwayDays ?? 0, unit: "days" }, gap: { label: "Cash gap to planning runway", kind: "money", value: metrics.bufferGapPaise, unit: "currency_minor" }, expectedDelta: { label: "Emergency runway", before: metrics.runwayDays ?? 0, after: nextDays, unit: "days" } };
    }
    case "health-protection": return metrics.healthCostScenarioPaise !== null
      ? { consequence: { label: "Entered bill stress test", kind: "money", value: metrics.healthCostScenarioPaise, unit: "currency_minor" }, availableCapacity: { label: "Estimated usable cover", kind: "money", value: metrics.healthEstimatedCoverCapacityPaise, unit: "currency_minor" }, gap: { label: "Estimated household exposure", kind: "money", value: metrics.healthEstimatedHouseholdExposurePaise ?? 0, unit: "currency_minor" }, expectedDelta: { label: "Healthcare-cost exposure", before: metrics.healthEstimatedHouseholdExposurePaise ?? 0, after: 0, unit: "currency_minor" } }
      : { consequence: { label: "Potential consequence", kind: "text", value: "Healthcare costs may fall on household resources" }, availableCapacity: facts.healthCoverAmountPaise > 0 ? { label: "Reported cover", kind: "money", value: facts.healthCoverAmountPaise, unit: "currency_minor" } : { label: "People with recorded cover", kind: "count", value: household.memberCount - household.uncoveredHealthCount - household.unknownHealthCount, unit: "people" }, gap: { label: "People without confirmed protection", kind: "count", value: household.uncoveredHealthCount + household.unknownHealthCount, unit: "people" }, expectedDelta: { label: "Coverage unknowns", before: household.unknownHealthCount + healthMissingFacts(context).length, after: 0, unit: "state" } };
    case "life-protection": return { consequence: { label: "Starting support need", kind: "money", value: metrics.lifeCoverPlanningNeedPaise, unit: "currency_minor" }, availableCapacity: { label: "Reported death benefit", kind: "money", value: facts.lifeCoverAmountPaise, unit: "currency_minor" }, gap: { label: "Starting support gap", kind: "money", value: metrics.lifeCoverGapPaise, unit: "currency_minor" }, expectedDelta: { label: "Unquantified support need", before: lifeMissingFacts(context).length > 0 ? "Estimated" : "Confirmed", after: "Confirmed", unit: "state" } };
    case "debt": return { consequence: { label: "Outstanding debt", kind: "money", value: facts.outstandingDebtPaise, unit: "currency_minor" }, availableCapacity: { label: "Monthly income", kind: "money", value: facts.incomeMonthlyPaise, unit: "currency_minor" }, gap: { label: "Income used by debt payments", kind: "ratio", value: metrics.debtPaymentRatioBps === null ? 0 : Math.round(metrics.debtPaymentRatioBps / 100), unit: "percent" }, expectedDelta: { label: "Overdue payment status", before: facts.hasOverdueDebt === null || facts.hasOverdueDebt === undefined ? "Unknown" : facts.hasOverdueDebt ? "Overdue" : "Current", after: "Current", unit: "state" } };
    case "goals": return { consequence: { label: "Goal target", kind: "money", value: facts.goalTargetPaise, unit: "currency_minor" }, availableCapacity: { label: "Already set aside", kind: "money", value: facts.goalCurrentSavingsPaise, unit: "currency_minor" }, gap: { label: "Still to fund", kind: "money", value: metrics.goalFundingGapPaise, unit: "currency_minor" }, expectedDelta: { label: "Goal definition", before: facts.goalTargetYears > 0 ? "Measured" : "Missing", after: "Measured", unit: "state" } };
    case "future": return { consequence: { label: "Time horizon", kind: "text", value: "Long term" }, availableCapacity: { label: "Local arrangements", kind: "text", value: "Not yet documented" }, gap: { label: "Planning status", kind: "text", value: pillar.status === "unknown" ? "Unknown" : "Needs review" }, expectedDelta: { label: "Planning status", before: "Unknown", after: "Documented", unit: "state" } };
  }
}

function factsFor(pillar: PillarResult, context: FinancialRuleContext): FactReference[] {
  const { facts, household, metrics } = context;
  switch (pillar.id) {
    case "cash-flow": return [moneyFact("income", "Monthly income", facts.incomeMonthlyPaise), moneyFact("outgo", "Core monthly outgo", metrics.monthlyOutgoPaise)];
    case "safety-buffer": return [moneyFact("liquid-cash", "Accessible safety cash", facts.liquidCashPaise), moneyFact("outgo", "Core monthly outgo", metrics.monthlyOutgoPaise), countFact("earners", "Income earners", household.earnerCount)];
    case "health-protection": return [countFact("household-members", "People in household", household.memberCount), countFact("uncovered-health", "Without reported cover", household.uncoveredHealthCount), countFact("unknown-health", "Coverage unknown", household.unknownHealthCount), moneyFact("reported-health-cover", "Reported usable benefit limit", facts.healthCoverAmountPaise), moneyFact("health-stress-test", "Entered healthcare-cost stress test", metrics.healthCostScenarioPaise ?? 0), moneyFact("health-cost-share", "Entered deductible or co-pay", facts.healthProtectionDetails?.estimatedCostSharePaise ?? 0)];
    case "life-protection": return [countFact("dependants", "People financially supported", household.dependantCount), countFact("unprotected-earners", "Earners without reported protection", household.unprotectedEarnerCount), countFact("unknown-earner-cover", "Earner protection unknown", household.unknownEarnerProtectionCount), moneyFact("debt", "Outstanding debt", facts.outstandingDebtPaise), moneyFact("living-support", "Essential-expense support", metrics.lifeOngoingNeedsPaise), moneyFact("reported-cover", "Reported life benefit", facts.lifeCoverAmountPaise)];
    case "debt": return [moneyFact("debt-payments", "Required monthly debt payments", facts.debtPaymentsMonthlyPaise), moneyFact("debt-balance", "Outstanding debt", facts.outstandingDebtPaise), moneyFact("income", "Monthly income", facts.incomeMonthlyPaise)];
    case "goals": return [moneyFact("goal-target", "Goal target", facts.goalTargetPaise), moneyFact("goal-savings", "Already set aside", facts.goalCurrentSavingsPaise), { key: "goal-years", label: "Time horizon", value: facts.goalTargetYears * 12, unit: "months" }];
    case "future": return [{ key: "age-band", label: "Age band", value: facts.ageBand }, { key: "top-concern", label: "Priority selected", value: facts.topConcern }];
  }
}

function priorityBand(finding: RiskFinding, facts: FinancialFacts): number {
  if ((finding.pillar === "cash-flow" || (finding.pillar === "debt" && facts.hasOverdueDebt)) && finding.state === "act_first") return 0;
  if (["health-protection", "life-protection"].includes(finding.pillar) && finding.state === "act_first") return 1;
  if (finding.pillar === "safety-buffer" && finding.state === "act_first") return 2;
  if (["health-protection", "life-protection"].includes(finding.pillar) && finding.state !== "protected") return 3;
  if (finding.pillar === "debt" && finding.state !== "protected") return 4;
  if (finding.pillar === "cash-flow" && finding.state !== "protected") return 5;
  if (finding.state === "unknown") return 6;
  if (finding.state === "review_soon") return 7;
  return 8;
}

function vulnerabilityScore(finding: RiskFinding, household: HouseholdSnapshot): number {
  return household.members.filter((member) => finding.affectedMemberIds.includes(member.id)).reduce((score, member) => score + (["0-12", "13-17", "65-74", "75+"].includes(member.ageBand) ? 2 : member.financiallyDependent ? 1 : 0), 0);
}

function buildRiskFinding(pillar: PillarResult, quest: Quest, context: FinancialRuleContext, ruleSet: CountryRuleSet): RiskFinding {
  const copy = riskCopy(pillar, context);
  const scenario = scenarioValues(pillar, context);
  const missingFacts = genericMissingFacts(pillar, context);
  const resolvedState = missingFacts.length > 0 && riskState(pillar.status) === "protected" ? "review_soon" : riskState(pillar.status);
  return {
    id: `risk-${pillar.id}`,
    pillar: pillar.id,
    outcome: outcomeForPillar[pillar.id],
    hazard: hazardForPillar[pillar.id],
    affectedMemberIds: affectedMembers(pillar, context.household),
    state: resolvedState,
    severity: severityFor(pillar),
    confidence: missingFacts.length > 0 ? "unknown" : riskConfidence(pillar.confidence),
    timeHorizon: horizonFor(pillar),
    headline: copy.headline,
    explanation: copy.explanation,
    ...scenario,
    factsUsed: factsFor(pillar, context),
    missingFacts,
    assumptions: quest.explainability.assumptions,
    nextAction: { questId: quest.id, title: quest.title, description: quest.description, effortMinutes: quest.effortMinutes, successText: quest.successText, impactLabel: scenario.expectedDelta.label, evidenceModes: pillar.id === "future" || pillar.id === "goals" ? ["fact-updated", "real-world-action", "accepted-risk"] : ["fact-updated", "document-checked", "real-world-action", "accepted-risk"] },
    expectedDelta: scenario.expectedDelta,
    sourceIds: quest.explainability.sourceIds,
    rulePackVersion: ruleSet.version
  };
}

function buildRiskSummary(findings: RiskFinding[]): RiskSummary {
  const weights: Record<RiskConfidence, number> = { confirmed: 100, estimated: 65, unknown: 25 };
  return {
    actFirst: findings.filter((finding) => finding.state === "act_first").length,
    reviewSoon: findings.filter((finding) => finding.state === "review_soon").length,
    protected: findings.filter((finding) => finding.state === "protected").length,
    unknown: findings.filter((finding) => finding.state === "unknown").length,
    dataConfidencePercent: Math.round(findings.reduce((total, finding) => total + weights[finding.confidence], 0) / Math.max(1, findings.length)),
    confirmedAreas: findings.filter((finding) => finding.confidence === "confirmed").length,
    totalAreas: findings.length
  };
}

function buildOutcomes(findings: RiskFinding[]): OutcomeSummary[] {
  const definitions: Array<Pick<OutcomeSummary, "id" | "label" | "question">> = [
    { id: "today", label: "Today", question: "Can the household meet normal expenses?" },
    { id: "shock", label: "Withstand a shock", question: "Can it absorb income loss or an emergency?" },
    { id: "protect", label: "Protect people", question: "Are the people relying on this household protected?" },
    { id: "future", label: "Build the future", question: "Are goals and long-term plans measurable?" }
  ];
  return definitions.map((definition) => {
    const matching = findings.filter((finding) => finding.outcome === definition.id);
    return { ...definition, state: matching.toSorted((left, right) => stateRank[left.state] - stateRank[right.state])[0]?.state ?? "unknown", findingIds: matching.map((finding) => finding.id) };
  });
}

const CONTENT_COVERAGE_AUDIT = {
  articleCount: 500,
  extractedWordCount: 1_082_888,
  publisherCount: 14,
  topicCount: 36,
  completedOn: "2026-09-08"
} as const;

type CoverageDefinition = Omit<CoverageArea, "state" | "summary"> & {
  reviewKey?: keyof NonNullable<FinancialFacts["extendedReview"]>;
};

const COVERAGE_DEFINITIONS: readonly CoverageDefinition[] = [
  { id: "monthly-money", label: "Monthly money", source: "fast_scan", countryScope: "universal", researchTopics: ["cash flow", "budgeting", "banking", "inflation"] },
  { id: "emergency-income-shock", label: "Emergency & income shocks", source: "fast_scan", countryScope: "universal", researchTopics: ["emergency reserves", "irregular income", "job loss"] },
  { id: "health-costs", label: "Health costs", source: "fast_scan", countryScope: "country_dependent", researchTopics: ["health protection", "medical-cost shocks", "policy quality"] },
  { id: "life-disability", label: "Life & disability", source: "fast_scan", countryScope: "country_dependent", researchTopics: ["life protection", "disability income", "family dependants"] },
  { id: "debt-credit", label: "Debt & credit", source: "fast_and_deep_scan", countryScope: "universal", reviewKey: "creditAndBorrowing", researchTopics: ["debt", "credit cards", "credit profile"] },
  { id: "goals-education-housing", label: "Goals, education & housing", source: "fast_scan", countryScope: "universal", researchTopics: ["goals", "education", "housing", "major purchases"] },
  { id: "retirement", label: "Retirement & pensions", source: "deep_scan", countryScope: "country_dependent", reviewKey: "retirementPlanning", researchTopics: ["retirement", "pensions", "inflation"] },
  { id: "investing", label: "Investing readiness", source: "deep_scan", countryScope: "universal", reviewKey: "investingReadiness", researchTopics: ["investing basics", "allocation", "diversification", "fees"] },
  { id: "tax-benefits", label: "Tax & public benefits", source: "deep_scan", countryScope: "country_dependent", reviewKey: "taxAndBenefits", researchTopics: ["tax planning", "public benefits"] },
  { id: "property-liability", label: "Property & liability", source: "deep_scan", countryScope: "country_dependent", reviewKey: "propertyAndLiability", researchTopics: ["property protection", "liability", "housing"] },
  { id: "estate-continuity", label: "Estate & beneficiaries", source: "deep_scan", countryScope: "country_dependent", reviewKey: "estateAndBeneficiaries", researchTopics: ["estate continuity", "nominees", "beneficiaries"] },
  { id: "family-care", label: "Family care & transitions", source: "deep_scan", countryScope: "country_dependent", reviewKey: "familyCareTransitions", researchTopics: ["elder care", "children", "relationships", "giving"] },
  { id: "fraud-security", label: "Fraud & digital safety", source: "deep_scan", countryScope: "universal", reviewKey: "fraudAndDigitalSafety", researchTopics: ["fraud", "identity theft", "account security"] },
  { id: "cross-border", label: "Cross-border money", source: "deep_scan", countryScope: "country_dependent", reviewKey: "crossBorderMoney", researchTopics: ["currency", "residency", "international money"] },
  { id: "records-reviews", label: "Records & periodic reviews", source: "deep_scan", countryScope: "universal", reviewKey: "recordsAndReviews", researchTopics: ["records", "renewals", "annual reviews"] }
];

const findingForCoverage: Partial<Record<CoverageAreaId, PillarId>> = {
  "monthly-money": "cash-flow",
  "emergency-income-shock": "safety-buffer",
  "health-costs": "health-protection",
  "life-disability": "life-protection",
  "debt-credit": "debt",
  "goals-education-housing": "goals"
};

function stateFromFinding(finding: RiskFinding | undefined): CoverageState {
  if (!finding || finding.state === "unknown") return "not_yet_checked";
  if (finding.state === "act_first" || finding.state === "review_soon") return "needs_attention";
  return "checked";
}

function stateFromReview(answer: DeepReviewAnswer | undefined, countryDependent: boolean): CoverageState {
  if (!answer || answer === "unknown") return "not_yet_checked";
  if (answer === "needs-attention") return "needs_attention";
  if (answer === "not-applicable") return "not_applicable";
  return countryDependent ? "local_guidance_needed" : "self_reviewed";
}

function coverageSummaryFor(state: CoverageState, definition: CoverageDefinition, finding?: RiskFinding): string {
  switch (state) {
    case "needs_attention": return finding?.state === "act_first" || finding?.state === "review_soon" ? finding.headline : "You marked this area as needing attention. Add the relevant facts before choosing a product or action.";
    case "checked": return finding?.headline ?? "The fast scan found no current exposure from the facts entered. Recheck after a material change.";
    case "self_reviewed": return "You reported reviewing this area and finding no current action. MoneyMap has not independently verified the underlying documents.";
    case "not_applicable": return "You marked this area not applicable to the household. Revisit it after a household, work or residency change.";
    case "local_guidance_needed": return `You reviewed this area, but ${definition.label.toLowerCase()} depends on local rules that this country pack does not yet verify.`;
    case "not_yet_checked": return definition.source === "fast_scan" ? "The fast scan does not yet have enough information to classify this area." : "Not included in the three-minute scan. Complete the optional deep check when ready.";
  }
}

export function buildCoverageMap(facts: FinancialFacts, findings: RiskFinding[]): { areas: CoverageArea[]; summary: CoverageSummary } {
  const findingByPillar = new Map(findings.map((finding) => [finding.pillar, finding]));
  const areas = COVERAGE_DEFINITIONS.map((definition): CoverageArea => {
    const findingPillar = findingForCoverage[definition.id];
    const finding = findingPillar ? findingByPillar.get(findingPillar) : undefined;
    const findingState = findingPillar ? stateFromFinding(finding) : undefined;
    const reviewAnswer = definition.reviewKey ? facts.extendedReview?.[definition.reviewKey] : undefined;
    let state = definition.reviewKey
      ? stateFromReview(reviewAnswer, definition.countryScope === "country_dependent")
      : findingState ?? "not_yet_checked";

    if (findingState === "needs_attention") state = "needs_attention";
    else if (findingState === "checked" && definition.reviewKey) {
      if (!reviewAnswer || reviewAnswer === "unknown") state = "not_yet_checked";
      else if (reviewAnswer === "reviewed") state = "self_reviewed";
      else if (reviewAnswer === "not-applicable") state = "checked";
    }

    return { ...definition, state, summary: coverageSummaryFor(state, definition, finding) };
  });
  const count = (state: CoverageState) => areas.filter((area) => area.state === state).length;
  return {
    areas,
    summary: {
      needsAttention: count("needs_attention"),
      checked: count("checked"),
      selfReviewed: count("self_reviewed"),
      notYetChecked: count("not_yet_checked"),
      notApplicable: count("not_applicable"),
      localGuidanceNeeded: count("local_guidance_needed"),
      total: areas.length
    }
  };
}

function monthsToFund(gapPaise: number, monthlyCapacityPaise: number): number | null {
  if (gapPaise <= 0) return 0;
  if (monthlyCapacityPaise <= 0) return null;
  return Math.ceil(gapPaise / monthlyCapacityPaise);
}

export function buildDecisionPlan(facts: FinancialFacts, metrics: DerivedMetrics, findings: RiskFinding[]): DecisionPlan {
  const monthlyCapacityPaise = cappedMoney(Math.max(0, metrics.monthlySurplusPaise));
  const monthlyShortfallPaise = cappedMoney(Math.max(0, -metrics.monthlySurplusPaise));
  const firstBufferTargetPaise = metrics.monthlyOutgoPaise;
  const firstBufferGapPaise = cappedMoney(firstBufferTargetPaise - facts.liquidCashPaise);
  const goalMonths = facts.goalTargetYears > 0 ? facts.goalTargetYears * 12 : 0;
  const goalMonthlyCheckpointPaise = facts.primaryGoal !== "none" && metrics.goalFundingGapPaise > 0 && goalMonths > 0
    ? cappedMoney(Math.ceil(metrics.goalFundingGapPaise / goalMonths))
    : null;
  const goalCapacityGapPaise = goalMonthlyCheckpointPaise === null
    ? null
    : cappedMoney(goalMonthlyCheckpointPaise - monthlyCapacityPaise);
  const state: DecisionPlan["state"] = monthlyShortfallPaise > 0
    ? "monthly_shortfall"
    : firstBufferGapPaise > 0
      ? "build_first_month"
      : metrics.bufferGapPaise > 0
        ? "build_resilience"
        : "cash_resilience_funded";
  return {
    state,
    monthlyIncomePaise: facts.incomeMonthlyPaise,
    essentialExpensesPaise: facts.essentialExpensesMonthlyPaise,
    requiredDebtPaymentsPaise: facts.debtPaymentsMonthlyPaise,
    monthlyCapacityPaise,
    monthlyShortfallPaise,
    firstBufferTargetPaise,
    firstBufferGapPaise,
    monthsToFirstBufferAtCurrentCapacity: monthsToFund(firstBufferGapPaise, monthlyCapacityPaise),
    resilienceTargetPaise: metrics.bufferTargetPaise,
    resilienceGapPaise: metrics.bufferGapPaise,
    monthsToResilienceAtCurrentCapacity: monthsToFund(metrics.bufferGapPaise, monthlyCapacityPaise),
    goalMonthlyCheckpointPaise,
    goalCapacityGapPaise,
    topFindingId: findings[0]?.id ?? null
  };
}

export function runFinancialPlanEngine(ruleSet: CountryRuleSet, input: FinancialFacts, now = new Date()): FinancialPlan {
  const facts = FinancialFactsSchema.parse(input);
  const validationError = ruleSet.validateFacts(facts) ?? (!ruleSet.matchesCountry(facts.country) ? `The ${ruleSet.id} ruleset does not support ${facts.country}.` : null);
  if (validationError) throw new Error(validationError);
  const household = deriveHouseholdContext(facts);
  const metrics = deriveMetrics(facts, household, ruleSet.metrics);
  const context: FinancialRuleContext = { facts, household, metrics };
  const pillars = PILLAR_ORDER.map((id): PillarResult => ({ id, label: ruleSet.pillars[id].label, ...ruleSet.pillars[id].evaluate(context) }));
  const pillarById = new Map(pillars.map((pillar) => [pillar.id, pillar]));
  const questsUnsorted = PILLAR_ORDER.map((id): Quest => {
    const pillar = pillarById.get(id)!;
    const definition = ruleSet.pillars[id].quest(context, pillar);
    return { ...definition, pillar: id, priority: priorityForStatus(pillar.status), explainability: { ...definition.explainability, rulesetVersion: ruleSet.version } };
  });
  const questByPillar = new Map(questsUnsorted.map((quest) => [quest.pillar, quest]));
  const riskFindings = pillars.map((pillar) => buildRiskFinding(pillar, questByPillar.get(pillar.id)!, context, ruleSet)).toSorted((left, right) => priorityBand(left, facts) - priorityBand(right, facts) || severityRank[left.severity] - severityRank[right.severity] || right.affectedMemberIds.length - left.affectedMemberIds.length || vulnerabilityScore(right, household) - vulnerabilityScore(left, household) || horizonRank[left.timeHorizon] - horizonRank[right.timeHorizon] || confidenceGapRank[left.confidence] - confidenceGapRank[right.confidence] || left.nextAction.effortMinutes - right.nextAction.effortMinutes);
  const findingOrder = new Map(riskFindings.map((finding, index) => [finding.pillar, index]));
  const quests = questsUnsorted.toSorted((left, right) => (findingOrder.get(left.pillar) ?? 99) - (findingOrder.get(right.pillar) ?? 99) || pillarStatusRank[pillarById.get(left.pillar)!.status] - pillarStatusRank[pillarById.get(right.pillar)!.status]);
  const scored = pillars.filter((pillar): pillar is PillarResult & { score: number } => pillar.score !== null);
  const coverage = buildCoverageMap(facts, riskFindings);
  const decisionPlan = buildDecisionPlan(facts, metrics, riskFindings);
  return {
    id: crypto.randomUUID(),
    country: facts.country,
    currency: facts.currency,
    localizationStatus: ruleSet.localizationStatus,
    engineVersion: FINANCIAL_ENGINE_VERSION,
    rulesetVersion: ruleSet.version,
    rulePack: { schemaVersion: ruleSet.schemaVersion, reviewedOn: ruleSet.review.reviewedOn, nextReviewOn: ruleSet.review.nextReviewOn, unsupportedAreas: ruleSet.review.unsupportedAreas },
    generatedAt: now.toISOString(),
    headline: riskFindings[0]?.headline ?? "Build your financial foundation",
    overallScore: scored.length === 0 ? 0 : Math.round(scored.reduce((total, pillar) => total + pillar.score, 0) / scored.length),
    household,
    metrics,
    decisionPlan,
    pillars,
    riskSummary: buildRiskSummary(riskFindings),
    riskFindings,
    outcomes: buildOutcomes(riskFindings),
    coverageSummary: coverage.summary,
    coverageAreas: coverage.areas,
    contentCoverageAudit: CONTENT_COVERAGE_AUDIT,
    quests,
    sourceIds: [...new Set(quests.flatMap((quest) => quest.explainability.sourceIds))],
    disclaimer: ruleSet.disclaimer(context)
  };
}

export function scenarioForBuffer(metrics: DerivedMetrics, targetMonths: number): { targetPaise: MoneyPaise; gapPaise: MoneyPaise } {
  const targetPaise = cappedMoney(metrics.monthlyOutgoPaise * targetMonths);
  const currentCash = metrics.bufferTargetPaise - metrics.bufferGapPaise;
  return { targetPaise, gapPaise: cappedMoney(targetPaise - currentCash) };
}
